export type SoundEffect =
  | "ui-click"
  | "letter"
  | "delete"
  | "submit"
  | "countdown"
  | "warning-30"
  | "tick"
  | "notification"
  | "solved"
  | "result-win"
  | "result-loss"
  | "result-draw";

const ENABLED_STORAGE_KEY = "mk:sound-enabled";
const VOLUME_STORAGE_KEY = "mk:sound-volume";
const LAST_VOLUME_STORAGE_KEY = "mk:sound-last-volume";
const DEFAULT_VOLUME = 80;
const MAX_MASTER_GAIN = 3;

type SoundGroup = "interface" | "timer" | "result";

interface ActiveSource {
  node: OscillatorNode;
  group: SoundGroup;
}

interface PlayOptions {
  /** Kullanıcı hareketinin içindeyken AudioContext'i açmaya izin verir. */
  userGesture?: boolean;
}

type AudioContextConstructor = new () => AudioContext;

let volume = readStoredVolume();
let lastAudibleVolume = volume > 0 ? volume : readStoredLastAudibleVolume();
let context: AudioContext | null = null;
let master: GainNode | null = null;
let playbackGeneration = 0;
const activeSources = new Set<ActiveSource>();
const listeners = new Set<() => void>();

export function parseSoundPreference(value: string | null): boolean {
  return value !== "false";
}

export function parseSoundVolume(value: string | null, legacyEnabled: string | null): number {
  if (value !== null && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return Math.min(100, Math.max(0, Math.round(parsed)));
  }
  return parseSoundPreference(legacyEnabled) ? DEFAULT_VOLUME : 0;
}

function readStoredVolume(): number {
  if (typeof window === "undefined") return DEFAULT_VOLUME;
  try {
    return parseSoundVolume(
      window.localStorage.getItem(VOLUME_STORAGE_KEY),
      window.localStorage.getItem(ENABLED_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_VOLUME;
  }
}

function readStoredLastAudibleVolume(): number {
  if (typeof window === "undefined") return DEFAULT_VOLUME;
  try {
    const value = Number(window.localStorage.getItem(LAST_VOLUME_STORAGE_KEY));
    return Number.isFinite(value) && value > 0
      ? Math.min(100, Math.round(value))
      : DEFAULT_VOLUME;
  } catch {
    return DEFAULT_VOLUME;
  }
}

function persistPreference(value: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(value));
    window.localStorage.setItem(ENABLED_STORAGE_KEY, String(value > 0));
    if (value > 0) window.localStorage.setItem(LAST_VOLUME_STORAGE_KEY, String(value));
  } catch {
    // Depolama kapalıysa tercih yalnız bu sayfa açıkken korunur.
  }
}

export function getSoundEnabled(): boolean {
  return volume > 0;
}

export function getSoundVolume(): number {
  return volume;
}

export function subscribeSoundPreference(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setSoundEnabled(value: boolean): void {
  setSoundVolume(value ? lastAudibleVolume : 0);
}

function masterGainFor(value: number): number {
  if (value <= 0) return 0;
  return MAX_MASTER_GAIN * Math.pow(value / 100, 1.5);
}

export function setSoundVolume(value: number): void {
  const next = Math.min(100, Math.max(0, Math.round(value)));
  if (volume === next) return;
  volume = next;
  if (next > 0) lastAudibleVolume = next;
  persistPreference(next);
  if (master && context) master.gain.setValueAtTime(masterGainFor(next), context.currentTime);
  if (next === 0) stopAllSounds();
  for (const listener of listeners) listener();
}

function audioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === "undefined") return null;
  const audioWindow = window as typeof window & {
    webkitAudioContext?: AudioContextConstructor;
  };
  return window.AudioContext ?? audioWindow.webkitAudioContext ?? null;
}

function ensureContext(): AudioContext | null {
  if (context && context.state !== "closed") return context;
  const Context = audioContextConstructor();
  if (!Context) return null;
  try {
    context = new Context();
    master = context.createGain();
    master.gain.value = masterGainFor(volume);
    master.connect(context.destination);
    return context;
  } catch {
    context = null;
    master = null;
    return null;
  }
}

function groupFor(effect: SoundEffect): SoundGroup {
  if (effect === "countdown" || effect === "warning-30" || effect === "tick") return "timer";
  if (effect === "solved" || effect.startsWith("result-")) return "result";
  return "interface";
}

function tone(
  ctx: AudioContext,
  group: SoundGroup,
  frequency: number,
  offset: number,
  duration: number,
  volume: number,
  type: OscillatorType = "triangle",
  endFrequency?: number,
): void {
  if (!master) return;
  const start = ctx.currentTime + offset;
  const stop = start + duration;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();
  const source: ActiveSource = { node: oscillator, group };

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  if (endFrequency !== undefined) {
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, stop);
  }
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.linearRampToValueAtTime(volume, start + Math.min(0.008, duration / 3));
  gain.gain.exponentialRampToValueAtTime(0.0001, stop);
  oscillator.connect(gain);
  gain.connect(master);
  oscillator.addEventListener(
    "ended",
    () => {
      activeSources.delete(source);
      oscillator.disconnect();
      gain.disconnect();
    },
    { once: true },
  );
  activeSources.add(source);
  oscillator.start(start);
  oscillator.stop(stop + 0.01);
}

function synthesize(ctx: AudioContext, effect: SoundEffect): void {
  const group = groupFor(effect);
  switch (effect) {
    case "ui-click":
      tone(ctx, group, 680, 0, 0.035, 0.11, "triangle", 560);
      break;
    case "letter":
      tone(ctx, group, 500, 0, 0.052, 0.13, "triangle", 650);
      break;
    case "delete":
      tone(ctx, group, 270, 0, 0.07, 0.13, "triangle", 175);
      break;
    case "submit":
      tone(ctx, group, 320, 0, 0.055, 0.12, "triangle", 380);
      tone(ctx, group, 440, 0.06, 0.075, 0.11, "triangle", 520);
      break;
    case "countdown":
      tone(ctx, group, 660, 0, 0.11, 0.15, "sine", 760);
      break;
    case "warning-30":
      tone(ctx, group, 500, 0, 0.13, 0.15, "sine", 570);
      tone(ctx, group, 690, 0.18, 0.2, 0.15, "sine", 760);
      break;
    case "tick":
      tone(ctx, group, 980, 0, 0.028, 0.09, "square", 760);
      break;
    case "notification":
      tone(ctx, group, 587.33, 0, 0.12, 0.13, "sine");
      tone(ctx, group, 783.99, 0.12, 0.18, 0.14, "sine");
      tone(ctx, group, 987.77, 0.26, 0.2, 0.12, "sine");
      break;
    case "solved":
      tone(ctx, group, 523.25, 0, 0.16, 0.13, "sine");
      tone(ctx, group, 659.25, 0.13, 0.18, 0.14, "sine");
      tone(ctx, group, 783.99, 0.27, 0.2, 0.15, "sine");
      tone(ctx, group, 1046.5, 0.43, 0.24, 0.13, "sine");
      break;
    case "result-win":
      tone(ctx, group, 523.25, 0, 0.14, 0.12, "sine");
      tone(ctx, group, 659.25, 0.13, 0.16, 0.13, "sine");
      tone(ctx, group, 783.99, 0.28, 0.19, 0.14, "sine");
      tone(ctx, group, 1046.5, 0.45, 0.25, 0.13, "sine");
      break;
    case "result-loss":
      tone(ctx, group, 392, 0, 0.25, 0.08, "sine", 349.23);
      tone(ctx, group, 293.66, 0.2, 0.33, 0.07, "sine", 261.63);
      break;
    case "result-draw":
      tone(ctx, group, 440, 0, 0.17, 0.1, "sine");
      tone(ctx, group, 554.37, 0.16, 0.22, 0.09, "sine");
      break;
  }
}

/**
 * Otomatik sesler yalnız daha önce açılmış bir context'te çalar. Kullanıcı
 * hareketleri context'i açabilir; tarayıcı reddederse geriye dönük kuyruk yoktur.
 */
export function playSound(effect: SoundEffect, options: PlayOptions = {}): void {
  if (volume <= 0) return;
  const ctx = options.userGesture ? ensureContext() : context;
  if (!ctx || !master) return;

  if (ctx.state === "running") {
    synthesize(ctx, effect);
    return;
  }
  if (!options.userGesture) return;

  const generation = playbackGeneration;
  const requestedAt = window.performance.now();
  void ctx
    .resume()
    .then(() => {
      if (
        volume <= 0 ||
        generation !== playbackGeneration ||
        ctx !== context ||
        ctx.state !== "running" ||
        window.performance.now() - requestedAt > 250
      ) {
        return;
      }
      synthesize(ctx, effect);
    })
    .catch(() => undefined);
}

function stopGroup(group?: SoundGroup): void {
  for (const source of [...activeSources]) {
    if (group && source.group !== group) continue;
    activeSources.delete(source);
    try {
      source.node.stop();
    } catch {
      // Zaten bitmiş bir oscillator güvenle yok sayılır.
    }
  }
}

export function stopTimerSounds(): void {
  stopGroup("timer");
}

export function stopAllSounds(): void {
  playbackGeneration += 1;
  stopGroup();
}

function eligibleButton(target: EventTarget | null): HTMLButtonElement | null {
  if (!(target instanceof Element)) return null;
  const button = target.closest("button");
  if (!(button instanceof HTMLButtonElement)) return null;
  if (button.disabled || button.getAttribute("aria-disabled") === "true") return null;
  if (button.closest(".key") || button.hasAttribute("data-sound-control")) return null;
  return button;
}

/** Native düğmelere tek, merkezi klik sesi bağlar. */
export function installButtonSounds(): () => void {
  if (typeof document === "undefined") return () => undefined;

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0 || !eligibleButton(event.target)) return;
    playSound("ui-click", { userGesture: true });
  };
  const onClick = (event: MouseEvent) => {
    // Klavye ile etkinleştirilen düğmeler pointerdown üretmez.
    if (event.detail !== 0 || !eligibleButton(event.target)) return;
    playSound("ui-click", { userGesture: true });
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("click", onClick, true);
  return () => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("click", onClick, true);
  };
}

/** Yalnız testlerin tarayıcı durumunu birbirinden ayırması için. */
export function resetSoundForTests(): void {
  stopAllSounds();
  context = null;
  master = null;
  playbackGeneration = 0;
  volume = DEFAULT_VOLUME;
  lastAudibleVolume = DEFAULT_VOLUME;
}
